import documentDownload from "@assets/images/document-download.svg";
import documentDuplicate from "@assets/images/document-duplicate.svg";
import mailFilled from "@assets/images/mail-filled.svg";
import pencilWhite from "@assets/images/pencil-white.svg";
import plusTealIcon from "@assets/images/plus-teal.svg";
import saveIcon from "@assets/images/save-icon-white.svg";
import { Button, Paper, Tooltip } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import { ChangeOrderCSTab } from "@saleor/changeOrderDomestic/views/ViewEditOrder/types";
import { ViewEditMode } from "@saleor/changeOrderExport/views/ViewEditOrder/types";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import { menuWidth, shrunkMenuWidth } from "@saleor/components/Siderbar";
import { PermissionEnum } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import {
  formatThousandDecimal,
  shortenLongDecimal
} from "@saleor/utils/number";
import classNames from "classnames";
import React, { useMemo } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

interface propsType {
  expandMenu?: boolean;
  bodyPaddingRight?: number;
}

const useStyles = makeStyles<propsType>(
  theme => ({
    menu: {
      zIndex: 100,
      padding: 0,
      position: "fixed",
      bottom: 0,
      right: 0,
      transition: "width 0.4s ease",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        padding: "0 2.4rem",
        paddingRight: props => `calc(2.4rem + ${props.bodyPaddingRight}px)`,
        width: props =>
          props.expandMenu
            ? `calc(100% - ${menuWidth}px)`
            : `calc(100% - ${shrunkMenuWidth}px)`
      }
    },
    wrapper: {
      display: "block",
      width: "100%",
      alignItems: "center",
      boxShadow: "0px -8px 24px rgb(0 0 0 / 8%)",
      borderRadius: "20px 20px 0px 0px"
    },
    section: {
      display: "flex",
      padding: "1.6rem 2.4rem",
      justifyContent: "space-between",
      gap: "0 2.4rem"
    },
    divider: {
      margin: 0,
      border: 0,
      borderTop: `1px dashed ${theme.palette.grey[500]}`
    },
    summaryCaption: {
      color: theme.palette.grey[900]
    },
    number: {
      fontSize: "1.8rem",
      lineHeight: "2.4rem"
    },
    sectionRight: {
      display: "flex"
    },
    price: {
      minWidth: 152,
      marginRight: "2.4rem",
      "&:last-child": {
        marginRight: 0,
        minWidth: "75px"
      }
    },
    priceCaption: {
      color: theme.palette.grey[600],
      marginBottom: "0.6rem"
    },
    netPriceNumber: {
      color: theme.palette.info.dark
    },
    btnAddProduct: {
      alignItems: "center",
      height: 48,
      fontSize: "1.6rem",
      padding: "0 13px",
      "& .MuiButton-label": {
        gap: "0 8px",
        "& .MuiTypography-root": {
          fontWeight: 700
        }
      },
      "&.Mui-disabled .MuiTypography-root , &.Mui-disabled .MuiButton-startIcon": {
        color: theme.palette.grey[300]
      }
    },
    btnConfirmOrder: {
      alignItems: "center",
      paddingLeft: 40,
      paddingRight: 40,
      minWidth: 200,
      color: theme.palette.info.main,
      "&.Mui-disabled .MuiTypography-root , &.Mui-disabled .MuiButton-startIcon": {
        color: theme.palette.grey[300]
      }
    },
    btnSubmit: {
      background:
        "linear-gradient(90deg, #601863 0%, #681661 12%, #81125C 32%, #A90C55 57%, #DF044A 86%, #FD0045 100%)",
      borderRadius: "4px",
      height: 48,
      fontSize: "1.6rem",
      padding: "0 25px",
      "& .MuiTypography-root": {
        color: "#FFFFFF"
      }
    },
    leftContainer: {
      display: "flex",
      alignItems: "center",
      gap: "1.6rem",
      "& button": {
        height: 48,
        "&.Mui-disabled": {
          "& .MuiButton-label": {
            opacity: 0.3
          }
        }
      }
    },
    rightContainer: {
      display: "flex",
      gap: "1.6rem"
    },
    buttonContainedCommon: {
      padding: 12,
      width: 180,
      height: 48,
      fontSize: 16,
      lineHeight: "24px",
      "& .MuiButton-label": {
        fontWeight: 700
      }
    }
  }),
  { name: "CreateOrderFooter" }
);

interface CreateOrderFooterProps {
  activeTab: ChangeOrderCSTab;
  disabled?: boolean;
  disabledUpdate?: boolean;
  disabledAddMore?: boolean;
  hasValidationError?: boolean;
  priceData: {
    itemCount: number;
    orderAmtAfterVat: number;
    orderAmtVat: number;
    orderAmtBeforeVat: number;
    currency: string;
  };
  onSubmit: () => void;
  onPopup: () => void;
  onApplyAll: () => void;
  onCancelOrder: () => void;
  onCancelItem: () => void;
  onATP: () => void;
  onSendMail: () => void;
  onDownload: () => void;
  onDuplicate: () => void;
  onCancelAddNewItem: () => void;
  onChangeMode: (mode: ViewEditMode) => void;
  loading: boolean;
  status?: string;
  listSelect: string[];
  taxPercent: number;
  splitLineId: string;
  handleCancelEdit: () => void;
  isFlagR5?: boolean;
  isProductCateg1?: boolean;
  disabledActionForCashSale?: boolean;
}

const ChangeOrderFooter: React.FC<CreateOrderFooterProps> = props => {
  const {
    activeTab,
    disabled,
    priceData,
    onSubmit,
    onPopup,
    onChangeMode,
    onCancelItem,
    onCancelOrder,
    onATP,
    onSendMail,
    onDownload,
    onDuplicate,
    onCancelAddNewItem,
    loading,
    status,
    listSelect,
    taxPercent,
    splitLineId,
    isFlagR5,
    isProductCateg1,
    disabledActionForCashSale,
    handleCancelEdit
  } = props;
  const [{ expandMenu, bodyPaddingRight, viewEditMode }, _] = useAppState();
  const { user } = useUser();

  const isChangeOrderDomestic = useMemo(
    () =>
      !!user.userPermissions.find(
        e => e.code === PermissionEnum.CHANGE_ORDER_DOMESTIC
      ),
    [user.userPermissions]
  );
  const classes = useStyles({
    expandMenu,
    bodyPaddingRight
  });
  const intl = useIntl();
  const {
    itemCount,
    orderAmtAfterVat,
    orderAmtVat,
    orderAmtBeforeVat,
    currency
  } = priceData;

  const ButtonAddItem = () => (
    <Button
      id="addProductButton"
      data-testid="addProductButton"
      variant="outlined"
      color="secondary"
      onClick={onPopup}
      className={classes.btnAddProduct}
      disabled={
        !isChangeOrderDomestic ||
        status === "Complete Order" ||
        status === "Completed Delivery" ||
        isFlagR5 ||
        disabledActionForCashSale
      }
      startIcon={<SVG src={plusTealIcon} />}
    >
      <MyTypography variant={"body2"}>
        {intl.formatMessage({
          defaultMessage: "เพิ่มรายการสินค้า"
        })}
      </MyTypography>
    </Button>
  );

  const ButtonSubmitOrder = () => (
    <Button
      id="saveOrder"
      data-testid="saveOrder"
      variant={"contained"}
      color={"secondary"}
      onClick={onSubmit}
      disabled={!!splitLineId}
      className={classes.btnSubmit}
      startIcon={<SVG src={saveIcon} />}
    >
      <MyTypography variant={"body2"}>
        <b>
          {intl.formatMessage({
            defaultMessage: "บันทึกคำสั่งซื้อ"
          })}
        </b>
      </MyTypography>
    </Button>
  );

  return (
    <div className={classes.menu} data-testid="changeOrderFooter">
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "สรุปยอดคำสั่งซื้อ"
              })}
            </MyTypography>
            <MyTypography variant="body1" className={classes.number}>
              ({itemCount}{" "}
              {itemCount >= 2
                ? intl.formatMessage({ defaultMessage: "items" })
                : intl.formatMessage({ defaultMessage: "item" })}
              )
            </MyTypography>
          </div>
          <div className={classes.sectionRight}>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมสุทธิ"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {orderAmtBeforeVat ? (
                  <DecimalFormat
                    value={+orderAmtBeforeVat}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {`ภาษีมูลค่าเพิ่ม ${shortenLongDecimal(taxPercent * 100)}%`}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {orderAmtVat ? (
                  <DecimalFormat
                    value={+orderAmtVat}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม"
                })}
              </MyTypography>
              <MyTypography
                variant="subtitle1"
                className={classes.netPriceNumber}
              >
                <span className={classes.totalPrice}>
                  {orderAmtAfterVat
                    ? formatThousandDecimal(+orderAmtAfterVat, 2)
                    : "0.00"}
                </span>
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "Currency"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {currency}
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.section}>
          <div className={classes.leftContainer}>
            {viewEditMode === ViewEditMode.VIEW ? (
              <>
                {status !== "Cancelled" ? (
                  <Tooltip title={"Send Email"} placement={"top"}>
                    <div>
                      <Button
                        id="sendEmailButton"
                        data-testid="sendEmailButton"
                        variant={"outlined"}
                        color={"secondary"}
                        onClick={onSendMail}
                        disabled={!isChangeOrderDomestic || loading}
                      >
                        <img src={mailFilled} alt="email-filled-icon" />
                      </Button>
                    </div>
                  </Tooltip>
                ) : (
                  ""
                )}
                <Tooltip title={"Download PDF"} placement={"top"}>
                  <div>
                    <Button
                      id="downloadPdfButton"
                      data-testid="downloadPdfButton"
                      variant={"outlined"}
                      color={"secondary"}
                      onClick={onDownload}
                      disabled={!isChangeOrderDomestic || loading}
                    >
                      <img
                        src={documentDownload}
                        alt="document-download-icon"
                      />
                    </Button>
                  </div>
                </Tooltip>
                <Tooltip title={"Copy Order"} placement={"top"}>
                  <div>
                    <Button
                      id="copyOrderButton"
                      data-testid="copyOrderButton"
                      variant={"outlined"}
                      color={"secondary"}
                      onClick={onDuplicate}
                      disabled={!isChangeOrderDomestic || loading}
                    >
                      <img
                        src={documentDuplicate}
                        alt="document-duplicate-icon"
                      />
                    </Button>
                  </div>
                </Tooltip>
              </>
            ) : (
              ""
            )}
            {viewEditMode === ViewEditMode.EDIT ? (
              <>
                {/* {activeTab === ChangeOrderCSTab.header ? (
                  <Button
                    variant="contained"
                    className={classNames(classes.buttonContainedCommon, "defaultB")}
                    onClick={onApplyAll}
                    disabled={disabled}
                  >
                    {intl.formatMessage({
                      defaultMessage: "APPLY ALL",
                      description: "button label"
                    })}
                  </Button>
                ) : (
                  ""
                )} */}
                {activeTab === ChangeOrderCSTab.itemDetails ? (
                  <>
                    <Button
                      id="cancelItemButton"
                      data-testid="cancelItemButton"
                      variant="outlined"
                      color="primary"
                      onClick={onCancelItem}
                      disabled={
                        disabled ||
                        !listSelect.length ||
                        isFlagR5 ||
                        disabledActionForCashSale
                      }
                    >
                      <MyTypography variant={"body2"}>
                        Cancel Item (<b>{listSelect.length}</b>)
                      </MyTypography>
                    </Button>
                    {isProductCateg1 && (
                      <Button
                        id="atpCtpButton"
                        data-testid="atpCtpButton"
                        variant="outlined"
                        onClick={onATP}
                        className={"defaultB"}
                        disabled={disabled || !listSelect.length || isFlagR5}
                      >
                        <MyTypography variant={"body2"}>
                          ATP/CTP (<b>{listSelect.length}</b>)
                        </MyTypography>
                      </Button>
                    )}
                  </>
                ) : (
                  ""
                )}
              </>
            ) : (
              ""
            )}
            {viewEditMode === ViewEditMode.ADD ? (
              <Button
                id="cancelAddNewItemButton"
                data-testid="cancelAddNewItemButton"
                variant="outlined"
                className={classNames(
                  classes.buttonContainedCommon,
                  "defaultB"
                )}
                onClick={onCancelAddNewItem}
                disabled={disabled}
              >
                {intl.formatMessage({
                  defaultMessage: "ยกเลิกเพิ่มรายการ",
                  description: "button label"
                })}
              </Button>
            ) : (
              ""
            )}
          </div>
          <div className={classes.rightContainer}>
            {viewEditMode === ViewEditMode.VIEW && status !== "Cancelled" ? (
              <>
                <Button
                  id="cancelOrderButton"
                  data-testid="cancelOrderButton"
                  variant="outlined"
                  color="primary"
                  onClick={onCancelOrder}
                  disabled={
                    !isChangeOrderDomestic ||
                    isFlagR5 ||
                    disabledActionForCashSale
                  }
                >
                  <MyTypography variant={"body2"}>
                    {intl.formatMessage({ defaultMessage: "ยกเลิกคำสั่งซื้อ" })}
                  </MyTypography>
                </Button>
                <ButtonAddItem />
                <Button
                  id="changeOrderButton"
                  data-testid="changeOrderButton"
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={() => onChangeMode(ViewEditMode.EDIT)}
                  disabled={
                    !isChangeOrderDomestic ||
                    (activeTab !== ChangeOrderCSTab.header &&
                      status === "Complete Order")
                  }
                  className={classes.btnConfirmOrder}
                  startIcon={<SVG src={pencilWhite} />}
                >
                  <MyTypography variant={"body2"}>
                    <b>
                      {intl.formatMessage({
                        defaultMessage: "แก้ไขคำสั่งซื้อ"
                      })}
                    </b>
                  </MyTypography>
                </Button>
              </>
            ) : (
              ""
            )}
            {viewEditMode === ViewEditMode.EDIT ? (
              <>
                <Button
                  id="cancelChangeOrderButton"
                  data-testid="cancelChangeOrderButton"
                  variant={"outlined"}
                  className={classNames(
                    classes.buttonContainedCommon,
                    "defaultB"
                  )}
                  color={"secondary"}
                  onClick={handleCancelEdit}
                >
                  <MyTypography variant={"body2"}>
                    {intl.formatMessage({ defaultMessage: "ยกเลิกแก้ไข" })}
                  </MyTypography>
                </Button>
                <ButtonSubmitOrder />
              </>
            ) : (
              ""
            )}
            {viewEditMode === ViewEditMode.ADD ? (
              <>
                <ButtonAddItem />
                <ButtonSubmitOrder />
              </>
            ) : (
              ""
            )}
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default ChangeOrderFooter;
